import { ref, computed, watch } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { ICreditCardAddressFormData } from "@/apps/credit-line/types/forms";
import { useValidateShippingCoverage } from "./useValidateShippingCoverage";
import { useCityRegionData } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/useCityRegionData";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useShippingAddressForm(
  coverageValidation: string,
  deviceTag?: string,
  cityFieldForm?: string,
  regionFieldForm?: string
) {
  const { t } = useI18n();
  const { zoneWithCoverage, validateCoverage } = useValidateShippingCoverage();

  // Form fields
  const streetField = ref<string>("");
  const streetFieldError = ref<string>("");
  const streetNumberField = ref<string>("");
  const streetNumberFieldError = ref<string>("");
  const apartmentFieldError = ref<string>("");
  const apartmentField = ref<string>("");
  const floorField = ref<string>("");
  const postalCodeField = ref<string>("");
  const postalCodeFieldError = ref<string>("");
  const neighborhoodField = ref<string>("");
  const neighborhoodFieldError = ref<string>("");
  const cityField = ref<string>("");
  const cityFieldError = ref<string>("");
  const regionField = ref<string>("");
  const regionFieldError = ref<string>("");
  const notesField = ref<string>("");
  const isZipCodeUserProvided = ref<boolean>(false);
  const source = ref<string>("");
  const isProposedAddressSelected = ref(false);
  const shippingAttemptId = ref<string | null>(null);
  const isGoogleAutocompleteShown = ref<boolean>(true);
  const isModalOpen = ref(false);

  const highlightFields = ref<boolean>(false);
  const touchedStreet = ref<boolean>(false);
  const cityFieldFormRef = ref<string>(cityFieldForm || "");
  const regionFieldFormRef = ref<string>(regionFieldForm || "");

  const { cityRegionData } = useCityRegionData();

  const filteredCities = computed(() => {
    const cityFieldValue = cityFieldFormRef.value.toLowerCase() || "";
    if (!cityFieldValue) {
      return [
        ...new Set(
          cityRegionData.value.map((item) => `${item.city} (${item.region})`)
        ),
      ];
    }
    return [
      ...new Set(
        cityRegionData.value
          .filter((item) => item.city.toLowerCase().includes(cityFieldValue))
          .map((item) => `${item.city} (${item.region})`)
      ),
    ];
  });

  const handleCitySelection = async (selectedOption: string) => {
    const match = selectedOption.match(/^(.*) \((.*)\)$/);
    if (match) {
      const [, city, region] = match;
      cityFieldFormRef.value = city;
      regionFieldFormRef.value = region;

      await checkCoverageZone();
      if (zoneWithCoverage.error) {
        isModalOpen.value = true;
      }
    } else {
      cityFieldFormRef.value = selectedOption;
      regionFieldFormRef.value = "";
    }
  };

  const disabledBtn = computed(() => {
    return (
      [
        streetField.value,
        cityField.value,
        postalCodeField.value,
        apartmentField.value,
        streetNumberField.value,
      ].some((e) => e === "") || zoneWithCoverage.isLoading
    );
  });

  const checkCoverageZone = async (city?: string, municipality?: string) => {
    if (coverageValidation === "regionAndCity") {
      const cityToCheck = city || cityFieldFormRef.value;
      const regionToCheck = municipality || regionFieldFormRef.value;
      const data = await validateCoverage({
        deviceTag: deviceTag || "",
        city: cityToCheck,
        region: regionToCheck,
      });

      if (!data) return;
      if (data.isZoneWithCoverage) {
        if (!cityField.value || cityField.value.trim() === "") {
          cityField.value = data.city || "";
        }
        if (!neighborhoodField.value || neighborhoodField.value.trim() === "") {
          neighborhoodField.value = data.municipality || "";
        }
      }
    }
  };

  const validateRequiredFields = async () => {
    await checkCoverageZone(cityField.value, regionField.value);
    const requiredFieldError = t("PAYJOY_CARD_VIEWS.FORM_REQUIRED_FIELD");
    streetFieldError.value = streetField.value === "" ? requiredFieldError : "";
    streetNumberFieldError.value =
      streetNumberField.value === "" ? requiredFieldError : "";
    postalCodeFieldError.value =
      postalCodeField.value === "" ? requiredFieldError : "";
    neighborhoodFieldError.value =
      neighborhoodField.value === "" ? requiredFieldError : "";
    cityFieldError.value = cityField.value === "" ? requiredFieldError : "";
    regionFieldError.value = regionField.value === "" ? requiredFieldError : "";
  };

  const getFormattedNumber = (numberField: string) => {
    if (numberField == "0" || !numberField) {
      return t("PAYJOY_CARD_VIEWS.NONUMBER");
    }
    return numberField;
  };

  const getFormData = (): Partial<ICreditCardAddressFormData> => ({
    street: streetField.value.substring(0, 100),
    streetNumber: getFormattedNumber(streetNumberField.value),
    apartment: apartmentField.value || t("PAYJOY_CARD_VIEWS.NOINTNUMBER"),
    floor: floorField.value || "NA",
    postalCode: postalCodeField.value,
    neighborhood: neighborhoodField.value,
    city: cityField.value,
    region: regionField.value || "NA",
    notes: notesField.value || "NA",
    isZipCodeUserProvided: isZipCodeUserProvided.value,
    source: source.value,
    isProposedAddressSelected: isProposedAddressSelected.value,
    isGoogleAutocompleteShown: isGoogleAutocompleteShown.value,
    shippingAttemptId:
      shippingAttemptId.value !== null
        ? String(shippingAttemptId.value)
        : undefined,
  });

  watch(streetField, () => {
    zoneWithCoverage.error = "";
  });

  return {
    // Form fields
    streetField,
    streetFieldError,
    streetNumberField,
    streetNumberFieldError,
    apartmentField,
    apartmentFieldError,
    floorField,
    postalCodeField,
    postalCodeFieldError,
    neighborhoodField,
    neighborhoodFieldError,
    cityField,
    cityFieldError,
    regionField,
    regionFieldError,
    notesField,

    // Process logs
    isZipCodeUserProvided,
    source,
    isProposedAddressSelected,
    shippingAttemptId,
    isGoogleAutocompleteShown,

    // UI state
    highlightFields,
    touchedStreet,
    disabledBtn,
    zoneWithCoverage,

    // Methods
    validateRequiredFields,
    getFormData,
    filteredCities,
    handleCitySelection,
    checkCoverageZone,
    isModalOpen,
  };
}
